import {DTO_STATE, IS_DELETED, UNIT_OF_WEIGHT} from '@mybake/ui/common/dictionary';
import {IRecipe} from '../../../models/Recipe/IRecipe';
import {IRecipePart} from '../../../models/Recipe/IRecipePart';
import {IRecipePartItem} from '../../../models/Recipe/IRecipePartItem';
import {IProportion} from '../../../models/Recipe/IProportion';
import {IFilling} from '../../../models/Recipe/Filling/IFilling';
import {IDecorationItem} from '../../../models/Decorations/IDecorationItem';
import {RecipeGuidDto, RecipePartGuidDto, SaveRecipeGuidRequest, SaveRecipePartGuidRequest} from '../../../models/Recipe/IRecipeGuid';

export const newId = () => crypto.randomUUID();

export const makeProportion = (itemId: number | string, quantity?: number): IProportion => ({
    id: newId(),
    recipePartItemId: itemId,
    quantity: quantity ?? 0,
    proportion: 1,
    state: DTO_STATE.DTO_NEW,
});

export const makeRecipeItem = (partId?: number | string, fillingId?: number | string): IRecipePartItem => {
    const id = newId();
    return {
        id,
        itemId: -1,
        recipePartId: partId,
        fillingId,
        itemName: '',
        unitOfWeight: UNIT_OF_WEIGHT.G,
        isDeleted: IS_DELETED.ACTIVE,
        state: DTO_STATE.DTO_NEW,
        proportions: [makeProportion(id)],
    };
};

export const makeRecipePart = (recipeId: number | string): IRecipePart => ({
    id: newId(),
    recipeId,
    partName: '',
    partDescription: '',
    items: [makeRecipeItem()],
    isDeleted: IS_DELETED.ACTIVE,
    state: DTO_STATE.DTO_NEW,
    createdAt: null,
});

export const makeRecipe = (): IRecipe => {
    const id = newId();
    return {
        id,
        recipeName: '',
        recipeDescription: '',
        parts: [],
        isDeleted: IS_DELETED.ACTIVE,
        state: DTO_STATE.DTO_NEW,
        createdAt: null,
        price: 0,
        flagSpecial: false,
        isLinear: true,
        proportionInfos: [],
        recipeFillings: [],
        fillings: [],
        productTypeId: null,
    };
};

export const makeFilling = (): IFilling => ({
    id: newId(),
    fillingName: '',
    fillingDescription: '',
    isDeleted: IS_DELETED.ACTIVE,
    price: 0,
    state: DTO_STATE.DTO_NEW,
    createdAt: null,
    proportionInfos: [],
    items: [makeRecipeItem(undefined, newId())],
    recipes: [],
});

export const makeDecorationItem = (): IDecorationItem => ({
    id: newId(),
    itemId: -1,
    name: '',
    quantity: undefined as unknown as number,
    state: DTO_STATE.DTO_NEW,
    unit: UNIT_OF_WEIGHT.G,
});

export const trimOrEmpty = (value?: string | null) => (value ?? '').trim();

export const normalizedRecipeName = (value?: string | null) => trimOrEmpty(value).toLocaleLowerCase();

export const isDuplicateRecipeName = (name: string | undefined, names: string[], originalName?: string) => {
    const normalized = normalizedRecipeName(name);
    if (!normalized || (originalName !== undefined && normalized === normalizedRecipeName(originalName))) return false;
    return names.some(candidate => normalizedRecipeName(candidate) === normalized);
};

export const itemQuantity = (item: IRecipePartItem) => {
    const quantity = item.proportions?.[0]?.quantity;
    return quantity === undefined || quantity === null ? '' : String(quantity);
};

export const updatePrimaryProportionQuantity = (item: IRecipePartItem, quantity: number): IProportion[] => [{
    ...(item.proportions?.[0] ?? makeProportion(item.id)),
    quantity,
    state: item.proportions?.[0]?.state === DTO_STATE.DTO_NEW
        ? DTO_STATE.DTO_NEW
        : DTO_STATE.DTO_MODIFIED,
}];

/** Removes client-only metadata and normalizes temporary identifiers for RecipePart API commands. */
export const recipePartForApi = (part: IRecipePart, recipeId: number | string): IRecipePart => {
    const {clientOrigin, clientOriginalSnapshot, ...value} = part;
    const isNew = value.state === DTO_STATE.DTO_NEW;
    return {
        ...value,
        id: isNew ? 0 : value.id,
        recipeId,
        items: (value.items ?? []).map(item => ({
            ...item,
            id: isNew || item.state === DTO_STATE.DTO_NEW ? 0 : item.id,
            recipePartId: isNew ? 0 : value.id,
            proportions: (item.proportions ?? []).map(proportion => ({
                ...proportion,
                id: isNew || proportion.state === DTO_STATE.DTO_NEW ? 0 : proportion.id,
                recipePartItemId: isNew || item.state === DTO_STATE.DTO_NEW ? 0 : item.id,
            })),
        })),
    };
};

export const recipePartComparisonSnapshot = (part?: IRecipePart) => ({
    partName: trimOrEmpty(part?.partName),
    partDescription: trimOrEmpty(part?.partDescription),
    categoryId: part?.categoryId ?? null,
    items: (part?.items ?? [])
        .filter(item => item.state !== DTO_STATE.DTO_DELETED)
        .map(item => ({
            itemId: item.itemId,
            itemName: trimOrEmpty(item.itemName),
            unitOfWeight: item.unitOfWeight ?? '',
            weightOfPiece: item.weightOfPiece ?? null,
            proportions: (item.proportions ?? []).map(proportion => ({
                quantity: proportion.quantity ?? null,
                proportion: proportion.proportion ?? null,
            })),
        })),
});

/**
 * Builds a semantic snapshot for Edit dirty-state checks.
 * DTO metadata and generated client-side identifiers must not make an unchanged
 * recipe look modified, while added/removed parts and ingredients still do.
 */
export const recipeComparisonSnapshot = (recipe?: IRecipe) => ({
    recipeName: trimOrEmpty(recipe?.recipeName),
    recipeDescription: trimOrEmpty(recipe?.recipeDescription),
    productTypeId: recipe?.productTypeId ?? null,
    flagSpecial: Boolean(recipe?.flagSpecial),
    isLinear: Boolean(recipe?.isLinear),
    fillingIds: (recipe?.recipeFillings ?? [])
        .filter(relation => relation.state !== DTO_STATE.DTO_DELETED)
        .map(relation => String(relation.fillingId))
        .sort(),
    parts: (recipe?.parts ?? [])
        .filter(part => part.state !== DTO_STATE.DTO_DELETED)
        .map(recipePartComparisonSnapshot),
});

/** Fields owned by SaveRecipe. Recipe parts are persisted through dedicated endpoints. */
export const recipeDetailsComparisonSnapshot = (recipe?: IRecipe) => ({
    recipeName: trimOrEmpty(recipe?.recipeName),
    recipeDescription: trimOrEmpty(recipe?.recipeDescription),
    productTypeId: recipe?.productTypeId ?? null,
    flagSpecial: Boolean(recipe?.flagSpecial),
    isLinear: Boolean(recipe?.isLinear),
    recipeFillings: (recipe?.recipeFillings ?? [])
        .filter(relation => relation.state !== DTO_STATE.DTO_DELETED)
        .map(relation => ({recipeId: relation.recipeId, fillingId: relation.fillingId}))
        .sort((left, right) => String(left.fillingId).localeCompare(String(right.fillingId))),
});

/** Adapts the v2 response to the existing editor's local state model. */
export const recipeGuidForForm = (recipe: RecipeGuidDto): IRecipe => ({
    id: recipe.id,
    recipeName: recipe.recipeName,
    recipeDescription: recipe.recipeDescription,
    productTypeId: recipe.productTypeId,
    productTypeName: recipe.productTypeName,
    flagSpecial: recipe.flagSpecial,
    isLinear: recipe.isLinear,
    parts: recipe.parts.map(part => recipePartGuidForForm(part, recipe.id)),
    price: recipe.price ?? 0,
    createdAt: recipe.createdAt ? new Date(recipe.createdAt) : null,
    isDeleted: IS_DELETED.ACTIVE,
    state: DTO_STATE.DTO_UNCHANGED,
    proportionInfos: [],
    recipeFillings: (recipe.fillings ?? []).map(filling => ({
        recipeId: recipe.id,
        fillingId: filling.id,
        recipeName: recipe.recipeName,
        fillingName: filling.name,
        state: DTO_STATE.DTO_UNCHANGED,
    })),
    fillings: [],
});

export const recipePartGuidForForm = (part: RecipePartGuidDto, recipeId: string | number = ''): IRecipePart => ({
    id: part.id,
    recipeId,
    partName: part.partName,
    partDescription: part.partDescription,
    categoryId: part.categoryId,
    categoryName: part.categoryName,
    isMainPart: part.isMainPart,
    usedByRecipesCount: part.usedByRecipesCount,
    items: part.items.map(item => ({
        id: item.id,
        itemId: item.itemId as unknown as number,
        recipePartId: part.id,
        itemName: item.itemName,
        unitOfWeight: item.unitOfWeight,
        quantity: item.quantity,
        weight: item.weight,
        weightOfPiece: item.weightOfPiece,
        isDeleted: IS_DELETED.ACTIVE,
        state: DTO_STATE.DTO_UNCHANGED,
        proportions: item.proportions.map(proportion => ({
            id: proportion.id,
            recipePartItemId: item.id,
            quantity: proportion.quantity,
            proportion: proportion.proportion,
            state: DTO_STATE.DTO_UNCHANGED,
        })),
    })),
    isDeleted: IS_DELETED.ACTIVE,
    state: DTO_STATE.DTO_UNCHANGED,
    createdAt: null,
});

export const recipeGuidRequest = (recipe: IRecipe, includeId = false): SaveRecipeGuidRequest => ({
    ...(includeId ? {id: String(recipe.id)} : {}),
    recipeName: trimOrEmpty(recipe.recipeName),
    recipeDescription: trimOrEmpty(recipe.recipeDescription),
    productTypeId: recipe.productTypeId ?? null,
    flagSpecial: Boolean(recipe.flagSpecial),
    isLinear: Boolean(recipe.isLinear),
    fillings: (recipe.recipeFillings ?? [])
        .filter(relation => relation.state !== DTO_STATE.DTO_DELETED)
        .map(relation => ({id: String(relation.fillingId), name: relation.fillingName ?? ''})),
});

/** PUT is a full replacement; deleted rows and all response/client metadata are omitted. */
export const recipePartGuidRequest = (part: IRecipePart): SaveRecipePartGuidRequest => ({
    partName: trimOrEmpty(part.partName),
    partDescription: trimOrEmpty(part.partDescription),
    categoryId: part.categoryId ?? null,
    items: (part.items ?? [])
        .filter(item => item.state !== DTO_STATE.DTO_DELETED)
        .map(item => ({
            itemId: String(item.itemId),
            proportions: (item.proportions ?? []).map(proportion => ({
                quantity: Number(proportion.quantity),
                proportion: Number(proportion.proportion),
            })),
        })),
});
